<template>
  <div>
    <div v-if="viewMode === viewModes.OVERVIEW">
      <div>
        <h5>{{ $t('message.DomainDiagnoseOverview') }}</h5>

        <CAlert color="info" v-if="inProcess(this.domainList)" class="loading-message">
          <CSpinner />
          <div style="margin-left: 10px;">{{ $t('message.batch_diagnose_in_progress') }}</div>
        </CAlert>

        <p v-if="hasError(this.domainList) && !inProcess(this.domainList)">{{
          $t('message.batch_diagnose_error_detected') }}</p>
        <p v-else-if="!inProcess(this.domainList)">{{ $t('message.everything_okay') }}</p>
        <CListGroup>
          <CListGroupItem class="domain-item d-flex justify-content-between align-items-center"
            v-for="(d, index) in domainList" :key="d.name" @click="viewDomain(index)">
            <span class="text-truncate" :title="d.name">{{ d.name }}</span>
            <CSpinner size="sm" v-if="d.status == 'processing'" />
            <CBadge v-if="d.status !== 'processing'" :color="d.status ? 'success' : 'danger'" shape="pill">{{ d.status ?
              'OK' : 'X' }}
            </CBadge>
          </CListGroupItem>
        </CListGroup>
      </div>
    </div>

    <div v-if="viewMode === viewModes.DOMAIN">
      <div class="domain-mode-navigation">
        <div>
          <CButton @click="handleBackToOverview">{{ $t('message.back_to_overview') }}</CButton>
        </div>

        <nav>
          <ul class="pagination">
            <li :class="{ 'page-item': true, 'disabled': viewDomainIndex === 0 }" @click="prevDomain()">
              <span class="page-link">‹</span>
            </li>
            <li class="page-item">
              <span class="page-link text-truncate" :title="selectedDomainPreviewName">
                {{ selectedDomainPreviewName }}
              </span>
            </li>
            <li :class="{ 'page-item': true, 'disabled': viewDomainIndex === (domainList.length - 1) }"
              @click="nextDomain()">
              <span class="page-link">›</span>
            </li>
          </ul>
        </nav>
      </div>
      <div class="diagnose-container">
        <h5>
          {{ $t('message.diagnose_results_domain', { domain: selectedDomainPreviewName }) }}
        </h5>
        <BatchDiagnoseDetail
          :dg="dg"
          :domain="selectedViewDomain"
          :isLoading="isLoading"
          :hideContactUs="true"
        />
      </div>
    </div>
  </div>
</template>

<style>
.loading-message {
  display: flex;
  align-items: center;
}

.domain-mode-navigation {
  display: flex;
  align-items: center;
}

.domain-mode-navigation nav {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

.domain-mode-navigation .pagination {
  margin-bottom: 0;
}

.domain-mode-navigation .page-link {
  color: gray;
  max-width: 200px;
}

.diagnose-container {
  margin-top: 30px;
}

.diagnose-container h5 {
  margin-left: 5px;
  word-break: break-all;
}

.domain-item {
  cursor: pointer;
}

.domain-item:hover {
  background-color: #f5f5f5;
}
</style>

<script>
import { isEmpty } from 'lodash';
import { DomainAction } from '@/utilities/websocket';
import { Websocket } from "@/plugins/websocket";
import BatchDiagnoseDetail from "./BatchDiagnoseDetail.vue"

export default {
  name: 'BatchDiagnose',
  props: {
    domains: Array,
    diagnose: Boolean
  },
  components: { BatchDiagnoseDetail },
  mounted() {
    this.openWs();
  },
  beforeDestroy() {
    this.closeWs();
  },
  data() {
    return {
      domainStatusMap: {},
      checking: false,
      viewMode: 'OVERVIEW',
      viewModes: {
        OVERVIEW: 'OVERVIEW',
        DOMAIN: 'DOMAIN'
      },
      viewDomainIndex: null,
      dg: {
        init: [null, null],
        cname_query: [null, null],
        cname_visit: [null, null],
        source_setup: [null, null],
        source_visit: [null, null],
        source_cname: [null, null],
        ssl_visit: [null, null],
        site_visit: [null, null],
        site_visit_v6: [null, null]
      },
      isLoading: false,
      domain_ws: null
    };
  },
  computed: {
    domainList() {
      if (!this.diagnose)
        return [];

      const domains = [];
      for (let d of this.domains) {
        const key = (d?._data?.domain_name ?? '').toString().replace(/-/g, '.');
        const domain = { name: key };
        if (Object.keys(this.domainStatusMap).includes(key)) {
          domain.status = this.isOkay(this.domainStatusMap[key]);
        } else {
          domain.status = "processing";
        }
        domains.push(domain);
      }
      return domains;
    },
    selectedViewDomain() {
      return this.domains[this.viewDomainIndex];
    },
    selectedDomainPreviewName() {
      return (this.selectedViewDomain?._data?.domain_name ?? '').toString().replace(/-/g, '.')
    },
    selectedViewDomainStatus() {
      return this.domainStatusMap[this.selectedViewDomain.name];
    }
  },
  methods: {
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    openWs() {
      this.domain_ws = new Websocket(`domain/`, null, {
        onAuthSuccess: function () {
          this.diagnoseDomains();
        }.bind(this)
      });

      this.domain_ws.onMessage = function (event, result) {
        if (result?.action != DomainAction.DOMAIN_CHECKER || !result?.data?.row?._data || !result?.data?.data) {
          return;
        }
        const key = result.data.row._data.domain_name.split('-').join('.');
        this.$set(this.domainStatusMap, key, result.data.data);
        this.dg = result.data.data;
        this.isLoading = false;
      }.bind(this)
    },
    closeWs() {
      if (this.domain_ws) this.domain_ws.close();
    },
    sendDeploy(action, domains) {
      this.domain_ws.sendMessage(action, domains);
    },
    hasError(domainList) {
      return domainList.some((d) => !d.status);
    },
    inProcess(domainList) {
      return domainList.some((d) => d.status == 'processing');
    },
    hasDomainStatusData() {
      return !isEmpty(this.domainStatusMap);
    },
    async diagnoseDomains() {
      for (let d of this.domains) {
        this.domain_ws.sendMessage(DomainAction.DOMAIN_CHECKER, {
          route_id: d._data.id,
          row: d,
        });
      }
    },
    isOkay(statusData) {
      const keys = Object.keys(statusData);
      const isAllPassed = keys.every((k) => {
        const data = statusData[k];
        return data[0] || false;
      });
      return isAllPassed;
    },
    viewDomain(index) {
      this.viewDomainIndex = index;
      this.viewMode = this.viewModes.DOMAIN;
      this.refresh();
    },
    handleBackToOverview() {
      this.viewMode = this.viewModes.OVERVIEW;
      this.viewDomainIndex = null;
    },
    nextDomain() {
      const topIndex = this.domainList.length - 1;
      if (this.viewDomainIndex < topIndex) {
        this.viewDomainIndex++;
        this.refresh();
      }
    },
    prevDomain() {
      if (this.viewDomainIndex > 0) {
        this.viewDomainIndex--;
        this.refresh();
      }
    },
    async refresh() {
      this.isLoading = true;
      this.domain_ws.sendMessage(DomainAction.DOMAIN_CHECKER, {
        route_id: this.selectedViewDomain._data.id,
        row: this.selectedViewDomain,
      });
    }
  }
}
</script>
